import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Code as CodeIcon,
  Security as SecurityIcon,
  BugReport as BugReportIcon,
  CloudUpload as CloudUploadIcon,
  Speed as SpeedIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';
import DevSecOpsTabs from '../../components/devsecops/DevSecOpsTabs';
import { useNavigate } from 'react-router-dom';
import DevSecOpsComponentCard, { DevSecOpsComponentDto } from '../../components/devsecops/DevSecOpsComponentCard';
import api, { ApiResponse, authService, User } from '../../services/api';
import { devsecopsApi } from '../../services/devsecopsApi';

interface PipelineStage {
  name: string;
  status: 'pending' | 'running' | 'success' | 'failed';
  duration: string;
  steps: string[];
  icon: React.ReactElement;
}

const DevSecOpsPipeline: React.FC = () => {
  const navigate = useNavigate();
  const [activeStage, setActiveStage] = useState<number>(0);
  const [components, setComponents] = useState<DevSecOpsComponentDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<User['role'] | undefined>(undefined);

  const handleBack = () => {
    navigate('/devsecops');
  };

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        setLoading(true);
        const response = await devsecopsApi.get<ApiResponse<DevSecOpsComponentDto[]>>(
          '/devsecops/components',
          {
            params: { page: 'pipeline' }
          }
        );

        const data = response.data.data || [];
        setComponents(data);
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load pipeline components';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchComponents();
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        setCurrentUserRole(user.role);
      } catch (_err) {
        // leave role undefined if not authenticated; cards will render read-only
      }
    };

    loadUser();
  }, []);

  const pipelineStages: PipelineStage[] = [
    {
      name: 'Code Checkout & Build',
      status: 'success',
      duration: '45s',
      icon: <CodeIcon />,
      steps: [
        'Clone repository from GitHub',
        'Install dependencies (npm install)',
        'Run TypeScript compilation',
        'Build frontend and backend',
        'Generate source maps'
      ]
    },
    {
      name: 'Unit & Integration Tests',
      status: 'success',
      duration: '2m 15s',
      icon: <BugReportIcon />,
      steps: [
        'Run Jest unit tests (backend)',
        'Run React component tests',
        'Execute integration tests',
        'Generate code coverage report',
        'Verify 80% coverage threshold'
      ]
    },
    {
      name: 'Security Scanning',
      status: 'success',
      duration: '1m 30s',
      icon: <SecurityIcon />,
      steps: [
        'Run npm audit for vulnerabilities',
        'Execute GitLeaks secrets scan',
        'OWASP dependency check',
        'Container image scanning',
        'Generate security report'
      ]
    },
    {
      name: 'Accessibility Testing',
      status: 'success',
      duration: '45s',
      icon: <VerifiedIcon />,
      steps: [
        'Run jest-axe accessibility tests',
        'Validate WCAG 2.0 AA compliance',
        'Check color contrast ratios',
        'Verify keyboard navigation',
        'Generate 508 compliance report'
      ]
    },
    {
      name: 'Platform Config Validation',
      status: 'success',
      duration: '15s',
      icon: <SpeedIcon />,
      steps: [
        'Validate CORS origins',
        'Check allowed domains',
        'Verify owner email domains',
        'Validate environment configs',
        'Ensure no debug endpoints in prod'
      ]
    },
    {
      name: 'Deploy to Environment',
      status: 'running',
      duration: '1m 45s',
      icon: <CloudUploadIcon />,
      steps: [
        'Build Docker container',
        'Push to Railway registry',
        'Deploy backend to Railway',
        'Deploy frontend to Cloudflare Pages',
        'Run smoke tests on deployment'
      ]
    }
  ];

  const pipelineMetrics = [
    { label: 'Build Success Rate', value: '98.5%', color: 'success' },
    { label: 'Avg Build Time', value: '6m 30s', color: 'info' },
    { label: 'Deployments Today', value: '12', color: 'primary' },
    { label: 'Failed Builds', value: '2', color: 'error' }
  ];

  const recentBuilds = [
    { id: '#1247', branch: 'main', status: 'success', time: '2m ago', duration: '6m 15s' },
    { id: '#1246', branch: 'feature/devsecops-pages', status: 'success', time: '15m ago', duration: '6m 45s' },
    { id: '#1245', branch: 'main', status: 'success', time: '1h ago', duration: '6m 30s' },
    { id: '#1244', branch: 'feature/menu-integration', status: 'failed', time: '2h ago', duration: '3m 20s' },
    { id: '#1243', branch: 'main', status: 'success', time: '3h ago', duration: '6m 25s' }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'running':
        return <PlayArrowIcon color="primary" />;
      default:
        return <PlayArrowIcon color="disabled" />;
    }
  };

  const getStatusColor = (status: string): "success" | "error" | "warning" | "info" | "default" => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to DevSecOps Program"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            CI/CD Pipeline
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Automated Build, Test, Security, and Deployment Pipeline
          </Typography>
        </Box>
      </Box>

      {/* DevSecOps Tabs */}
      <DevSecOpsTabs activeTab="pipeline" />

      {/* Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Pipeline Overview
          </Typography>
          <Typography variant="body1" paragraph>
            Our CI/CD pipeline implements automation-by-default with integrated security scanning, accessibility testing,
            and policy-as-code enforcement. Every commit triggers automated builds, tests, and deployments across
            dev, stage, and production environments.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Current Build:</strong> #1247 on branch <code>main</code> - All stages passed ✓
          </Alert>
        </CardContent>
      </Card>

      <Typography variant="h5" gutterBottom color="primary" fontWeight="bold" sx={{ mb: 3 }}>
        CI/CD Pipeline Capabilities & Controls
      </Typography>

      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading CI/CD pipeline components...
        </Typography>
      )}

      {error && (
        <Typography variant="body2" color="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {components.map((component: DevSecOpsComponentDto) => (
          <Grid item xs={12} md={6} key={component.key}>
            <DevSecOpsComponentCard
              component={component}
              currentUserRole={currentUserRole}
              onUpdated={(updated) => {
                setComponents((prev: DevSecOpsComponentDto[]) =>
                  prev.map((c: DevSecOpsComponentDto) => (c.key === updated.key ? updated : c))
                );
              }}
            />
          </Grid>
        ))}
      </Grid>

      {/* Pipeline Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {pipelineMetrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: '100%', border: '2px solid #003366' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" display="block">
                  {metric.label}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {metric.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Pipeline Stages */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            Pipeline Stages
          </Typography>
          <Typography variant="body2" paragraph color="text.secondary">
            Click on any stage to view detailed steps and execution logs
          </Typography>
          <Stepper activeStep={activeStage} orientation="vertical">
            {pipelineStages.map((stage, index) => (
              <Step key={index} completed={stage.status === 'success'}>
                <StepLabel
                  error={stage.status === 'failed'}
                  icon={stage.icon}
                  onClick={() => setActiveStage(index)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {stage.name}
                    </Typography>
                    <Chip
                      label={stage.status.toUpperCase()}
                      size="small"
                      color={getStatusColor(stage.status)}
                      icon={getStatusIcon(stage.status)}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {stage.duration}
                    </Typography>
                  </Box>
                </StepLabel>
                <StepContent>
                  {stage.status === 'running' && (
                    <LinearProgress sx={{ mb: 2 }} />
                  )}
                  <List dense>
                    {stage.steps.map((step, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon>
                          <CheckCircleIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText primary={step} />
                      </ListItem>
                    ))}
                  </List>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Recent Builds */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            Recent Builds
          </Typography>
          <List>
            {recentBuilds.map((build, index) => (
              <ListItem
                key={index}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <ListItemIcon>
                  {getStatusIcon(build.status)}
                </ListItemIcon>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body1" fontWeight="bold">
                      Build {build.id}
                    </Typography>
                    <Chip label={build.branch} size="small" variant="outlined" />
                    <Chip
                      label={build.status}
                      size="small"
                      color={getStatusColor(build.status)}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {build.time} • Duration: {build.duration}
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Container>
  );
};

export default DevSecOpsPipeline;
